import { FC, useState } from "react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import type { WorkflowOutcome } from "@bb/agent-contract"
import {
  deleteStaffFeedback,
  StaffFeedbackReason,
  submitStaffFeedback
} from "../../../api/staff-agent"

/**
 * Outcome feedback for an EXECUTED action (feedback target type "action").
 * The question is whether the change did what the user intended — not
 * whether the assistant's prose was good; that is the response rating.
 * A "No" here is high severity on the agent side (possible unintended
 * mutation), so it asks what the user had to do about it.
 */
const REASONS: { value: StaffFeedbackReason; label: string }[] = [
  { value: "unexpected_action", label: "Changed something I didn't expect" },
  { value: "incorrect", label: "The result is wrong" },
  { value: "incomplete", label: "Only did part of it" },
  { value: "other", label: "Other" }
]

const FOLLOW_UPS: { value: WorkflowOutcome; label: string }[] = [
  { value: "corrected", label: "I fixed it myself" },
  { value: "reversed", label: "I undid it" }
]

interface Props {
  actionId: string
  conversationId?: string
  initial?: {
    sentiment: "helpful" | "not_helpful"
    reasons: StaffFeedbackReason[]
  }
}

const ActionOutcomeFeedback: FC<Props> = ({
  actionId,
  conversationId,
  initial
}) => {
  const [sentiment, setSentiment] = useState(initial?.sentiment)
  const [reasons, setReasons] = useState<StaffFeedbackReason[]>(
    initial?.reasons ?? []
  )
  const [followUp, setFollowUp] = useState<WorkflowOutcome | undefined>()
  const [comment, setComment] = useState("")
  const [showDetails, setShowDetails] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (work: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (requestError: any) {
      setError(requestError?.message || "Could not save feedback")
    } finally {
      setBusy(false)
    }
  }

  const clear = () =>
    run(async () => {
      await deleteStaffFeedback(actionId, "action")
      setSentiment(undefined)
      setReasons([])
      setFollowUp(undefined)
    })

  const chooseYes = () =>
    sentiment === "helpful"
      ? clear()
      : run(async () => {
          await submitStaffFeedback({
            targetType: "action",
            targetId: actionId,
            conversationId,
            sentiment: "helpful",
            workflowOutcome: "completed"
          })
          setSentiment("helpful")
          setReasons([])
          setShowDetails(false)
        })

  const chooseNo = () => {
    if (sentiment === "not_helpful" && !showDetails) return clear()
    setShowDetails(true)
  }

  const submitNo = () =>
    run(async () => {
      await submitStaffFeedback({
        targetType: "action",
        targetId: actionId,
        conversationId,
        sentiment: "not_helpful",
        reasons,
        comment: comment.trim() || undefined,
        workflowOutcome: followUp
      })
      setSentiment("not_helpful")
      setShowDetails(false)
    })

  const toggleReason = (reason: StaffFeedbackReason) =>
    setReasons(current =>
      current.includes(reason)
        ? current.filter(item => item !== reason)
        : [...current, reason]
    )

  return (
    <div className="mt-2 border-t border-amber-200/70 pt-2">
      <div className="flex items-center gap-1">
        <span className="mr-1 text-[11px] text-neutral-500">
          Did this do what you intended?
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={chooseYes}
          aria-label="Action did what I intended"
          aria-pressed={sentiment === "helpful"}
          className={`rounded p-1 transition-colors ${
            sentiment === "helpful"
              ? "bg-emerald-50 text-emerald-700"
              : "text-neutral-400 hover:bg-white hover:text-neutral-600"
          }`}
        >
          <ThumbsUp size={14} />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={chooseNo}
          aria-label="Action did not do what I intended"
          aria-pressed={sentiment === "not_helpful"}
          className={`rounded p-1 transition-colors ${
            sentiment === "not_helpful"
              ? "bg-red-50 text-red-700"
              : "text-neutral-400 hover:bg-white hover:text-neutral-600"
          }`}
        >
          <ThumbsDown size={14} />
        </button>
      </div>

      {showDetails && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-white p-2.5">
          <p className="text-xs font-medium text-neutral-700">
            What went wrong?
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {REASONS.map(reason => (
              <button
                key={reason.value}
                type="button"
                aria-pressed={reasons.includes(reason.value)}
                onClick={() => toggleReason(reason.value)}
                className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                  reasons.includes(reason.value)
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-gray-200 bg-white text-neutral-600 hover:border-gray-300"
                }`}
              >
                {reason.label}
              </button>
            ))}
          </div>
          <p className="mt-2.5 text-xs font-medium text-neutral-700">
            What did you do about it?{" "}
            <span className="font-normal text-neutral-400">(optional)</span>
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {FOLLOW_UPS.map(option => (
              <button
                key={option.value}
                type="button"
                aria-pressed={followUp === option.value}
                onClick={() =>
                  setFollowUp(current =>
                    current === option.value ? undefined : option.value
                  )
                }
                className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                  followUp === option.value
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-gray-200 bg-white text-neutral-600 hover:border-gray-300"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            maxLength={1000}
            rows={2}
            onChange={event => setComment(event.target.value)}
            placeholder="Optional details—please don't include borrower personal information"
            className="mt-2 w-full resize-none rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={busy || reasons.length === 0}
              onClick={submitNo}
              className="rounded-lg bg-violet-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-40"
            >
              Send feedback
            </button>
            <button
              type="button"
              onClick={() => setShowDetails(false)}
              className="px-2 py-1.5 text-xs text-neutral-500 hover:text-neutral-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default ActionOutcomeFeedback
